import React from "react";
import { 
  FaFacebookF, 
  FaTwitter, 
  FaLinkedinIn, 
  FaInstagram, 
  FaMapMarkerAlt, 
  FaPhoneAlt, 
  FaEnvelope, 
  FaStar 
} from "react-icons/fa";
import { SiFiverr, SiTrustpilot } from "react-icons/si";
import logo from "../assets/logo.png";

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-300 py-16 px-6 border-t border-gray-800 font-sans">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">

        {/* Column 1: Branding */}
        <div className="flex flex-col">
          <h2 className="text-3xl font-extrabold text-blue-500 mb-4 tracking-tight">
            <img src={logo} height={150} alt="NexWys Logo" className="w-40 h-16 object-contain" />
          </h2>
          <p className="text-sm leading-relaxed mb-6 max-w-xs">
            Delivering professional web, mobile, and digital solutions globally. We turn your ideas into digital reality.
          </p>
          <p className="text-gray-500 text-xs mt-auto hidden lg:block">
            © {new Date().getFullYear()} NEXWYS — All Rights Reserved.
          </p>
        </div>

        {/* Column 2: Quick Links */}
        <div>
          <h3 className="text-white font-semibold text-lg mb-6 flex items-center gap-2">
            <span className="w-4 h-0.5 bg-blue-500 rounded-full"></span>
            Quick Links
          </h3>
          <ul className="space-y-3 text-sm">
            <li><a href="/contact" className="hover:text-blue-400 hover:translate-x-1 inline-block transition-transform duration-300">Contact Us</a></li>
            <li><a href="/about" className="hover:text-blue-400 hover:translate-x-1 inline-block transition-transform duration-300">About Us</a></li>
            <li><a href="/blog" className="hover:text-blue-400 hover:translate-x-1 inline-block transition-transform duration-300">Blog</a></li>
            <li><a href="/privacy" className="hover:text-blue-400 hover:translate-x-1 inline-block transition-transform duration-300">Privacy Policy</a></li>
            <li><a href="/terms" className="hover:text-blue-400 hover:translate-x-1 inline-block transition-transform duration-300">Terms of Service</a></li>
          </ul>
        </div>

        {/* Column 3: Contact Info & Socials */}
        <div>
          <h3 className="text-white font-semibold text-lg mb-6 flex items-center gap-2">
            <span className="w-4 h-0.5 bg-blue-500 rounded-full"></span>
            Connect
          </h3>
          <ul className="space-y-4 text-sm mb-8">
            <li className="flex items-start gap-3">
              <FaMapMarkerAlt className="text-blue-500 mt-1 flex-shrink-0 text-base" /> 
              <span>Farid Kot <br/>Pakpattan, Pakistan</span>
            </li>
            <li className="flex items-center gap-3">
              <FaPhoneAlt className="text-blue-500 flex-shrink-0 text-base" /> 
              <span>+92 300 0456809</span>
            </li>
            <li className="flex items-center gap-3">
              <FaEnvelope className="text-blue-500 flex-shrink-0 text-base" /> 
              <span>info@nexwys.com</span>
            </li>
          </ul>

          {/* Social Icons */}
          <div className="flex space-x-3">
            <a 
              href="https://www.facebook.com/people/Ray-Shahzad-Ahmad/100051944016034/?_rdr" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="bg-gray-800 p-2.5 rounded-lg hover:bg-[#1877F2] hover:text-white transition-all duration-300 shadow-sm"
              aria-label="Facebook"
            >
              <FaFacebookF size={16} />
            </a>
            <a 
              href="https://x.com/rayshahzadahmad?s=21" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="bg-gray-800 p-2.5 rounded-lg hover:bg-black hover:text-white transition-all duration-300 shadow-sm"
              aria-label="Twitter"
            >
              <FaTwitter size={16} />
            </a>
            <a 
              href="https://www.linkedin.com/in/shehzadray" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="bg-gray-800 p-2.5 rounded-lg hover:bg-[#0A66C2] hover:text-white transition-all duration-300 shadow-sm"
              aria-label="LinkedIn"
            >
              <FaLinkedinIn size={16} />
            </a>
            <a 
              href="https://www.instagram.com/shehzadrayy/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="bg-gray-800 p-2.5 rounded-lg hover:bg-gradient-to-tr hover:from-yellow-400 hover:via-pink-500 hover:to-purple-500 hover:text-white transition-all duration-300 shadow-sm"
              aria-label="Instagram"
            >
              <FaInstagram size={16} />
            </a>
          </div>
        </div>

        {/* Column 4: Trust Badges */}
        <div>
          <h3 className="text-white font-semibold text-lg mb-6 flex items-center gap-2">
            <span className="w-4 h-0.5 bg-blue-500 rounded-full"></span>
            Client Trust
          </h3>
          
          <div className="flex flex-col gap-4">
            {/* Fiverr Rating Badge */}
            <a
              href="https://www.fiverr.com/shehzadray"
              target="_blank"
              rel="noopener noreferrer"
              className="group block bg-gray-800/40 border border-gray-700/50 hover:border-green-500/50 rounded-xl p-4 transition-all duration-300 hover:bg-gray-800 hover:shadow-[0_4px_20px_rgba(34,197,94,0.1)]"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <SiFiverr className="text-green-500 text-2xl" />
                  <span className="text-white font-medium text-sm group-hover:text-green-400 transition-colors">Hire us on Fiverr</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex text-yellow-400 text-sm">
                  <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                </div>
                <span className="text-gray-400 text-xs font-medium">5.0 Rating</span>
              </div>
            </a>

            {/* Trustpilot Rating Badge */}
            <a
              href="https://www.trustpilot.com/review/nexwys.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group block bg-gray-800/40 border border-gray-700/50 hover:border-teal-500/50 rounded-xl p-4 transition-all duration-300 hover:bg-gray-800 hover:shadow-[0_4px_20px_rgba(20,184,166,0.1)]"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <SiTrustpilot className="text-teal-500 text-2xl" />
                  <span className="text-white font-medium text-sm group-hover:text-teal-400 transition-colors">Excellent</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex text-teal-500 text-sm">
                  <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                </div>
                <span className="text-gray-400 text-xs font-medium">4.9/5 Reviews</span>
              </div>
            </a>
          </div>
        </div>

      </div>

      {/* Mobile Copyright & Footer Note */}
      <div className="mt-16 pt-6 border-t border-gray-800 flex flex-col lg:flex-row justify-between items-center text-gray-500 text-xs max-w-7xl mx-auto">
        <div className="lg:hidden mb-3">© {new Date().getFullYear()} NEXWYS — All Rights Reserved.</div>
        <div>Designed and Developed with <span className="text-red-500 animate-pulse text-sm">❤️</span> by NEXWYS Team</div>
      </div>
    </footer>
  );
};

export default Footer;